import { useState, useCallback, useEffect } from 'react';
import dynamic from 'next/dynamic';
import Icon from '@/components/shared/Icons';
import { MAX_GUEST_ROOMS_LIMIT } from '@/constants/filterConstants';
import type { GuestRoomProps } from '@/components/search-bar/GuestRoomInput';
import { format } from 'date-fns';

const CarinaDateRangePicker = dynamic(() => import('@cvent/carina/components/DateRangePicker_NEXT'), { ssr: false });
const Button = dynamic(() => import('@/components/shared/button/Button'), { ssr: false });

function isDateValid(dateStr) {
  // eslint-disable-next-line no-restricted-globals
  return !isNaN(dateStr);
}

export default function GuestRoomContent({
  inputProps,
  labels,
  blockedDays,
  guestRoomDetails,
  locale,
  toggleGuestRoomDetails,
  datePickerOpen,
  setDatePickerOpen,
  setUserModifiedGRDate
}: GuestRoomProps): JSX.Element {
  const {
    checkIn,
    checkOut,
    roomsPerNight,
    nextMonth,
    previousMonth,
    startDialog,
    endDialog,
    dialog,
    dateRangeLabel,
    guestRoomPopperDoneLabel,
    decrementGuestRoomsAriaLabel,
    incrementGuestRoomsAriaLabel
  } = labels;
  const [guestRoomCount, setGuestRoomCount] = useState(Number(inputProps.value));

  const counter = useCallback(
    (op: string) => {
      if (op === 'decrement') {
        setGuestRoomCount(prev => {
          if (prev !== 0) {
            return prev - 1;
          }
          return 0;
        });
      } else {
        setGuestRoomCount(prev => {
          if (prev < MAX_GUEST_ROOMS_LIMIT) {
            return prev + 1;
          }
          return prev;
        });
      }
    },
    [setGuestRoomCount]
  );

  const setGuestRoomDetails = useCallback(() => {
    inputProps.onChange(guestRoomCount, true);
    if (toggleGuestRoomDetails) {
      toggleGuestRoomDetails();
    }
  }, [guestRoomCount, inputProps, toggleGuestRoomDetails]);

  const handleGuestRoomDates = useCallback(
    (start: Date, end: Date) => {
      setUserModifiedGRDate(true);
      guestRoomDetails?.onGuestRoomDateChange(start, end);
    },
    [guestRoomDetails, setUserModifiedGRDate]
  );

  const dateFormat = locale.formatLong?.date({ width: 'short' });

  let checkInDateValue =
    guestRoomDetails?.checkInDate && isDateValid(guestRoomDetails?.checkInDate)
      ? format(guestRoomDetails?.checkInDate, dateFormat, { locale })
      : '';
  let checkOutDateValue =
    guestRoomDetails?.checkOutDate && isDateValid(guestRoomDetails?.checkOutDate)
      ? format(guestRoomDetails?.checkOutDate, dateFormat, { locale })
      : '';
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (guestRoomDetails?.checkInDate === undefined) checkInDateValue = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (guestRoomDetails?.checkOutDate === undefined) checkOutDateValue = '';
  }, [checkInDateValue, guestRoomDetails?.checkInDate, checkOutDateValue, guestRoomDetails?.checkOutDate]);

  const resetDates = useCallback(() => {
    setUserModifiedGRDate(true);
    guestRoomDetails?.onGuestRoomDateChange(undefined, undefined);
  }, [guestRoomDetails, setUserModifiedGRDate]);

  const handlePopperDismiss = useCallback(() => {
    if (setDatePickerOpen) setDatePickerOpen(false);
  }, [setDatePickerOpen]);

  const handlePopperOpen = useCallback(() => {
    if (setDatePickerOpen) setDatePickerOpen(true);
  }, [setDatePickerOpen]);

  const inputPropsforDate = {
    type: 'text',
    className: 'caret-transparent',
    value: '',
    onClick: e => {
      e.preventDefault();
      if (setDatePickerOpen) {
        setDatePickerOpen(true);
      }
    },
    onKeyDown: e => {
      e.preventDefault();
      e.stopPropagation();
    },
    onChange: e => {
      e.preventDefault();
      e.stopPropagation();
    },
    onPaste: e => {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div className="py-4 lg:rounded-lg lg:bg-background lg:p-4">
      <div className="flex items-center overflow-y-hidden lg:w-80">
        <span className="w-full">{roomsPerNight}</span>
        <div className="flex w-full lg:w-auto">
          <Button
            id="minus"
            type="button"
            variant="neutral-white"
            aria-label={decrementGuestRoomsAriaLabel}
            title={decrementGuestRoomsAriaLabel}
            icon={<Icon id="minus-circle" className="h-5 w-5 fill-brand-60" />}
            onClick={() => counter('decrement')}
          />
          <input
            className="text-primary w-full rounded-lg border border-solid border-neutral-40 text-center text-b-base font-normal lg:w-14"
            name="custom-counter"
            value={guestRoomCount}
            min={0}
            max={inputProps.max}
            readOnly
          />
          <Button
            id="plus"
            type="button"
            variant="neutral-white"
            aria-label={incrementGuestRoomsAriaLabel}
            title={incrementGuestRoomsAriaLabel}
            icon={<Icon id="plus-circle" className="h-5 w-5 fill-brand-60" />}
            onClick={() => counter('increment')}
          />
        </div>
      </div>
      {guestRoomCount > 0 ? (
        <>
          <div className="pt-6">
            <CarinaDateRangePicker
              blockedDays={blockedDays}
              dialogAriaLabel={dialog}
              isOpen={datePickerOpen}
              endDialogToggleLabel={endDialog}
              endInputLabel={checkOut}
              id="guest-room-date-range"
              locale={locale}
              labelText={dateRangeLabel}
              hideLabel
              portal
              onOpen={handlePopperOpen}
              onDismiss={handlePopperDismiss}
              nextButtonAriaLabel={nextMonth}
              onChange={handleGuestRoomDates}
              prevButtonAriaLabel={previousMonth}
              startDialogToggleLabel={startDialog}
              startInputLabel={checkIn}
              numberOfMonths={1}
              endInputProps={{ ...inputPropsforDate, value: checkOutDateValue }}
              startInputProps={{ ...inputPropsforDate, value: checkInDateValue }}
              startDate={guestRoomDetails?.checkInDate}
              endDate={guestRoomDetails?.checkOutDate}
            />
          </div>
          {guestRoomDetails?.checkInDate && guestRoomDetails?.checkOutDate && (
            <button
              type="button"
              className="pt-2 text-b-sm text-brand-60"
              onClick={resetDates}
              aria-label={labels.guestRoomPopperResetDatesLabel}
            >
              {labels.guestRoomPopperResetDatesLabel}
            </button>
          )}
        </>
      ) : null}
      <div className="flex justify-end pt-6">
        <Button
          text={guestRoomPopperDoneLabel}
          appearance="lined"
          onClick={setGuestRoomDetails}
          aria-label={guestRoomPopperDoneLabel}
        />
      </div>
    </div>
  );
}
